/**
 * Audio lifecycle for Telnyx WebRTC calls.
 *
 * - Eén gedeeld <audio>-element (`ensureRemoteAudioElement`), door de SDK
 *   beheerd via `client.remoteElement` — zie de toelichting hieronder.
 * - `playRingtone()` / `stopRingtone()` drive a two-tone Web Audio ringtone
 *   for inbound calls (migrated out of the old phone-store).
 */

import { getCached } from './devices';
import { emit } from './events';

// ─── Shared remote audio element (SDK-managed attachment) ──────────
//
// We hand this element to the Telnyx SDK as `client.remoteElement`. The SDK
// attaches the remote MediaStream to it on the WebRTC `track` event, which
// avoids the race where `call.remoteStream` is still null at the moment the
// call flips to `active` (the old manual-attach approach missed the stream
// and produced one-way audio: remote party could hear us, we couldn't hear
// them). A single shared element is correct for a one-line softphone.

let sharedRemoteEl: HTMLAudioElement | null = null;

// ─── Audio-telemetrie ───────────────────────────────────────────────
//
// Elke onbedoelde pauze/stream-wissel van het gedeelde element is potentieel
// "gesprek lijkt verbroken" (incident 2026-07-07: geluid weg terwijl de call
// serverside doorliep; zonder events was de oorzaak onzichtbaar). Dedup per
// event-type (30s) zodat de events-tabel niet volloopt.

// clearRemoteAudio() pauzeert bewust (call-einde); die is geen anomalie.
let clearingIntentionally = false;

const lastAudioEventAt = new Map<string, number>();

function emitAudioEvent(
  type: 'audio_element_paused' | 'audio_element_stream_lost',
  extra?: Record<string, unknown>,
): void {
  const now = Date.now();
  if (now - (lastAudioEventAt.get(type) ?? 0) < 30_000) return;
  lastAudioEventAt.set(type, now);
  emit({
    type,
    source: 'client',
    metadata: {
      path: typeof window !== 'undefined' ? window.location.pathname : null,
      visibility: typeof document !== 'undefined' ? document.visibilityState : null,
      ...extra,
    },
  });
}

function wireAudioTelemetry(el: HTMLAudioElement): void {
  el.addEventListener('pause', () => {
    if (clearingIntentionally) return;
    emitAudioEvent('audio_element_paused');
  });
  // 'emptied' vuurt bij srcObject-wissel/reset, 'ended' als de stream stopt.
  el.addEventListener('emptied', () => {
    if (clearingIntentionally) return;
    // Stream-WISSEL is geen verlies: bij het aankoppelen van een nieuwe call
    // vuurt 'emptied' terwijl srcObject al de verse stream is (gezien bij elke
    // answer op 2026-07-07). Alleen alarmeren als er écht niets meer hangt.
    if (el.srcObject) return;
    emitAudioEvent('audio_element_stream_lost', { via: 'emptied' });
  });
  el.addEventListener('ended', () => {
    if (clearingIntentionally) return;
    emitAudioEvent('audio_element_stream_lost', { via: 'ended' });
  });
}

/** Watchdog-hulp (client.ts): status van het gedeelde element. */
export function getRemoteAudioHealth(): {
  exists: boolean;
  paused: boolean;
  hasStream: boolean;
} {
  if (!sharedRemoteEl) return { exists: false, paused: true, hasStream: false };
  return {
    exists: true,
    paused: sharedRemoteEl.paused,
    hasStream: sharedRemoteEl.srcObject !== null,
  };
}

/** Watchdog-autoheal: probeer een gepauzeerd element weer te laten spelen. */
export async function tryResumeRemoteAudio(): Promise<boolean> {
  if (!sharedRemoteEl) return false;
  try {
    await sharedRemoteEl.play();
    return !sharedRemoteEl.paused;
  } catch {
    return false;
  }
}

export function ensureRemoteAudioElement(): HTMLAudioElement {
  if (sharedRemoteEl && document.body.contains(sharedRemoteEl)) {
    return sharedRemoteEl;
  }
  const el = document.createElement('audio');
  el.id = 'telnyx-remote-shared';
  el.autoplay = true;
  document.body.appendChild(el);
  sharedRemoteEl = el;
  applyPersistedSink(el);
  wireAudioTelemetry(el);
  return el;
}

export function applyRemoteSpeakerSink(): void {
  if (sharedRemoteEl) applyPersistedSink(sharedRemoteEl);
}

export function clearRemoteAudio(): void {
  if (!sharedRemoteEl) return;
  clearingIntentionally = true;
  try {
    sharedRemoteEl.pause();
    sharedRemoteEl.srcObject = null;
  } catch {
    /* ignore */
  } finally {
    // De pause/emptied-events van deze bewuste clear vuren op een latere
    // task; ruime marge zodat ze niet als anomalie tellen.
    setTimeout(() => {
      clearingIntentionally = false;
    }, 250);
  }
}

function applyPersistedSink(el: HTMLAudioElement): void {
  const prefs = getCached();
  if (!prefs.speakerId) return;
  const elWithSink = el as HTMLAudioElement & {
    setSinkId?: (id: string) => Promise<void>;
  };
  if (typeof elWithSink.setSinkId === 'function') {
    elWithSink.setSinkId(prefs.speakerId).catch((err) => {
      console.warn('[telephony/audio] setSinkId failed', err);
    });
  }
}

// ─── Ringtone (two-tone, Web Audio API) ───────────────────────────

/**
 * Autoplay-vangnet + telemetrie voor de toongeneratoren.
 *
 * Zonder user-activation start een AudioContext in Chrome 'suspended': het
 * hele klankschema draait dan geluidloos (stille beltoon/ringback, geen
 * error). Dat gebeurt m.n. als de owner-tab een achtergrondtab is waar
 * nooit geklikt is. resume() herstelt het zodra de tab sticky activation
 * heeft; de events maken op afstand zichtbaar wat de browser doet wanneer
 * iemand "geen toon" meldt.
 */
/** Output-apparaat voor bel-/ringbacktonen: apart beltoon-apparaat indien gekozen, anders de gesprek-speaker. */
function toneSinkId(): string | null {
  const prefs = getCached();
  return prefs.ringtoneSpeakerId ?? prefs.speakerId;
}

/**
 * Per-JS-context identiteit voor de toon-telemetrie. Twee gelijktijdige
 * tone-starts met verschillende ctxId's = twee vensters/contexten (tab, PWA-
 * window, iframe, tweede profiel) — onmisbaar bij "rinkelt op twee
 * apparaten"-meldingen, want per context leeft een eigen apparaat-cache.
 */
const TONE_CONTEXT_ID = Math.random().toString(36).slice(2, 8);

function toneContextMeta(): Record<string, unknown> {
  let displayMode = 'browser';
  try {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      displayMode = 'standalone';
    }
  } catch {
    /* ignore */
  }
  return {
    ctxId: TONE_CONTEXT_ID,
    path: location.pathname,
    displayMode,
    visibility: document.visibilityState,
    toneLock: 'v1',
  };
}

function reportAndResume(
  kind: 'ringtone' | 'ringback',
  ctx: AudioContext,
  volume: number,
): void {
  const type = kind === 'ringback' ? 'ringback_started' : 'ringtone_started';
  const ua = (navigator as {
    userActivation?: { hasBeenActive?: boolean; isActive?: boolean };
  }).userActivation;
  const meta = (phase: string): Record<string, unknown> => ({
    phase,
    ctxState: ctx.state,
    volume,
    sinkId: toneSinkId(),
    hasBeenActive: ua?.hasBeenActive ?? null,
    isActive: ua?.isActive ?? null,
    ...toneContextMeta(),
  });
  emit({ type, source: 'client', metadata: meta('created') });
  if (ctx.state === 'suspended') {
    ctx
      .resume()
      .then(() => emit({ type, source: 'client', metadata: meta('after_resume') }))
      .catch((err) =>
        emit({
          type,
          source: 'client',
          metadata: { ...meta('resume_failed'), error: String(err) },
        }),
      );
  }
}

// ─── Tone player: Web Audio → <audio>-element met betrouwbare sink ──
//
// We renderen de gegenereerde toon NIET direct naar `ctx.destination` (dat
// opent meteen output op het OS-default-apparaat), maar in een
// MediaStreamAudioDestinationNode en spelen die af via een los <audio>-
// element dat we vastpinnen op het gekozen apparaat met de breed-
// ondersteunde `HTMLMediaElement.setSinkId` — vóór `play()`.
//
// Waarom: `AudioContext.setSinkId` is te nieuw (Chrome 110+, ontbreekt in
// Firefox/Safari) én schakelt de sink pas ná de start op het default-
// apparaat. Daardoor lekte de beltoon naar de computerspeakers naast de
// koptelefoon. Door nooit `ctx.destination` te raken komt de toon
// gegarandeerd uit één apparaat — hetzelfde principe als de gesprek-audio
// (`ensureRemoteAudioElement`).
//
// `strictSink` (voor de apparaat-test in instellingen): kan de sink niet
// gepind worden, dan klinkt er NIETS en meldt de test dat eerlijk — in
// plaats van stiekem op het default-apparaat te spelen (de oude tester-bug).

interface ToneHandle {
  ctx: AudioContext;
  gain: GainNode;
  el: HTMLAudioElement;
  interval: ReturnType<typeof setInterval> | null;
}

async function openTonePlayer(opts: {
  kind: 'ringtone' | 'ringback';
  volume: number;
  sinkId: string | null;
  strictSink?: boolean;
}): Promise<ToneHandle | null> {
  const ctx = new AudioContext();
  const gain = ctx.createGain();
  gain.gain.value = Math.max(0, Math.min(1, opts.volume));

  const dest = ctx.createMediaStreamDestination();
  gain.connect(dest); // NB: bewust NIET ctx.destination → geen default-lek.

  const el = document.createElement('audio');
  el.srcObject = dest.stream;
  document.body.appendChild(el);
  const handle: ToneHandle = { ctx, gain, el, interval: null };

  const elWithSink = el as HTMLAudioElement & {
    setSinkId?: (id: string) => Promise<void>;
  };
  if (opts.sinkId && typeof elWithSink.setSinkId === 'function') {
    try {
      // Sink eerst pinnen, dán pas afspelen: geen enkele burst op default.
      await elWithSink.setSinkId(opts.sinkId);
    } catch (err) {
      emit({
        type: opts.kind === 'ringback' ? 'ringback_started' : 'ringtone_started',
        source: 'client',
        metadata: { phase: 'sink_failed', sinkId: opts.sinkId, error: String(err) },
      });
      if (opts.strictSink) {
        closeTonePlayer(handle);
        return null;
      }
      // Productie-toon: default-output is beter dan stilte.
    }
  } else if (opts.sinkId && opts.strictSink) {
    // Browser zonder setSinkId kan het apparaat niet garanderen.
    closeTonePlayer(handle);
    return null;
  }

  el.play().catch(() => {
    /* autoplay-policy — lost op bij volgende user-gesture */
  });
  return handle;
}

function closeTonePlayer(p: ToneHandle | null): void {
  if (!p) return;
  if (p.interval) clearInterval(p.interval);
  try {
    p.el.pause();
    p.el.srcObject = null;
    p.el.remove();
  } catch {
    /* ignore */
  }
  p.ctx.close().catch(() => {});
}

/**
 * true zodra de context hoorbaar draait; wacht max ~400ms op resume.
 * De resume-promise van een suspended context zonder user-activation blijft
 * eeuwig hangen — vandaar de race, anders houdt een stille tab de toon-lock
 * van de hele tabgroep vast.
 */
async function ensureAudible(ctx: AudioContext): Promise<boolean> {
  if (ctx.state === 'running') return true;
  await Promise.race([
    ctx.resume().catch(() => {}),
    new Promise<void>((res) => setTimeout(res, 400)),
  ]);
  return (ctx.state as string) === 'running';
}

let ringGen = 0;
let ringPlayer: ToneHandle | null = null;

/** @returns true als de toon hoorbaar speelt (voor de toon-lock-failover). */
export async function playRingtone(): Promise<boolean> {
  const gen = ++ringGen;
  closeTonePlayer(ringPlayer);
  ringPlayer = null;
  try {
    const prefs = getCached();
    const p = await openTonePlayer({
      kind: 'ringtone',
      volume: prefs.ringtoneVolume ?? 0.3,
      sinkId: toneSinkId(),
    });
    if (!p) return false;
    if (gen !== ringGen) {
      // stopRingtone() kwam binnen terwijl we de sink pinden.
      closeTonePlayer(p);
      return false;
    }
    ringPlayer = p;

    const burst = () => {
      const osc1 = p.ctx.createOscillator();
      osc1.frequency.value = 440;
      osc1.connect(p.gain);
      osc1.start(p.ctx.currentTime);
      osc1.stop(p.ctx.currentTime + 0.4);

      const osc2 = p.ctx.createOscillator();
      osc2.frequency.value = 480;
      osc2.connect(p.gain);
      osc2.start(p.ctx.currentTime);
      osc2.stop(p.ctx.currentTime + 0.4);
    };

    burst();
    p.interval = setInterval(burst, 2000);
    reportAndResume('ringtone', p.ctx, p.gain.gain.value);
    return await ensureAudible(p.ctx);
  } catch {
    // Web Audio unavailable — silent fallback.
    return false;
  }
}

export function stopRingtone(): void {
  ringGen++;
  closeTonePlayer(ringPlayer);
  ringPlayer = null;
}

// ─── Ringback (uitgaand: NL-toon 425 Hz, 1s aan / 4s uit) ──────────
//
// De outbound bridge beantwoordt de browser-poot (A-leg) meteen en belt
// daarna pas de klant; Telnyx geeft daarbij geen early media terug. Zonder
// lokaal opgewekte ringback hoort de beller dus stilte tot de klant opneemt.

let ringbackGen = 0;
let ringbackPlayer: ToneHandle | null = null;

/** @returns true als de toon hoorbaar speelt (voor de toon-lock-failover). */
export async function playRingback(): Promise<boolean> {
  const gen = ++ringbackGen;
  closeTonePlayer(ringbackPlayer);
  ringbackPlayer = null;
  try {
    const prefs = getCached();
    const p = await openTonePlayer({
      kind: 'ringback',
      // Iets zachter dan de inbound-beltoon: dit speelt vlak bij je oor.
      volume: (prefs.ringtoneVolume ?? 0.3) * 0.6,
      sinkId: toneSinkId(),
    });
    if (!p) return false;
    if (gen !== ringbackGen) {
      closeTonePlayer(p);
      return false;
    }
    ringbackPlayer = p;

    const burst = () => {
      const osc = p.ctx.createOscillator();
      osc.frequency.value = 425;
      osc.connect(p.gain);
      osc.start(p.ctx.currentTime);
      osc.stop(p.ctx.currentTime + 1.0);
    };

    burst();
    p.interval = setInterval(burst, 5000);
    reportAndResume('ringback', p.ctx, p.gain.gain.value);
    return await ensureAudible(p.ctx);
  } catch (err) {
    // Web Audio unavailable — silent fallback, maar wel zichtbaar op afstand.
    emit({
      type: 'ringback_started',
      source: 'client',
      metadata: { phase: 'error', error: String(err) },
    });
    return false;
  }
}

export function stopRingback(): void {
  ringbackGen++;
  closeTonePlayer(ringbackPlayer);
  ringbackPlayer = null;
}

// ─── Apparaat-test (instellingen) ──────────────────────────────────
//
// Speelt een korte toon strikt op het opgegeven apparaat. Lukt het pinnen
// niet (apparaat weg, geen setSinkId-support), dan klinkt er niets en is de
// uitkomst false — de oude tester speelde dan stilletjes op default en
// "werkte" daardoor schijnbaar nooit op het gekozen apparaat.

export async function playTestTone(
  sinkId: string | null,
  volume: number,
  pattern: 'blip' | 'ring' = 'blip',
): Promise<boolean> {
  const p = await openTonePlayer({
    kind: 'ringtone',
    volume,
    sinkId,
    strictSink: sinkId !== null,
  });
  if (!p) return false;
  const audible = await ensureAudible(p.ctx);
  if (!audible) {
    closeTonePlayer(p);
    return false;
  }

  const t = p.ctx.currentTime;
  const tone = (freq: number, at: number, dur: number) => {
    const osc = p.ctx.createOscillator();
    osc.frequency.value = freq;
    osc.connect(p.gain);
    osc.start(t + at);
    osc.stop(t + at + dur);
  };
  let totalMs: number;
  if (pattern === 'ring') {
    // Exact de echte beltoon: twee dubbeltoon-bursts met 2s interval.
    tone(440, 0, 0.4);
    tone(480, 0, 0.4);
    tone(440, 2, 0.4);
    tone(480, 2, 0.4);
    totalMs = 2600;
  } else {
    tone(440, 0, 0.4);
    totalMs = 600;
  }
  await new Promise<void>((res) => setTimeout(res, totalMs));
  closeTonePlayer(p);
  return true;
}
